import {
  Typography,
  Button,
  TextareaAutosize,
  IconButton,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  Snackbar,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DescriptionIcon from "@mui/icons-material/Description";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";

const PdfDataPanel = ({
  selectedPdfData,
  pdfData,
  theme,
  tabIndex,
  handleTabChange,
  editingCell,
  setEditingCell,
  editedValues,
  setEditedValues,
  nestedTab,
  setNestedTab,
  hoveredRow,
  setHoveredRow,
  handleSave,
  handleView,
  handleSaveEdit,
  handleSaveNestedArrayEdit,
  handleSaveNestedObjectEdit,
  handleResetNestedArrayEdit,
  handleResetNestedObjectEdit,
  handleReset,
  handleFocus,
  getRowCount,
  openNested,
  overlayActionsSx,
  exportToCSV,
  exportToXML,
  exportToJSON,
  anchorEl,
  openMenu,
  handleClickMenu,
  handleCloseMenu,
  showSnackbar,
  setShowSnackbar,
  handleSBClose,
  formatFieldName,
  hasTables,
}) => {
  return (
    <>
      {/* Top buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mb: 1,
          mr: 2,
        }}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontFamily: "'Poppins', sans-serif",
            backgroundColor: "#2AD899",
          }}
        >
          Save
        </Button>

        <Button
          id="download-button"
          aria-controls={openMenu ? "download-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? "true" : undefined}
          onClick={handleClickMenu}
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontFamily: "'Poppins', sans-serif",
            backgroundColor: "#1E3899",
          }}
        >
          Download
        </Button>

        <Menu
          id="download-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          MenuListProps={{
            "aria-labelledby": "download-button",
          }}
        >
          <MenuItem
            onClick={() => {
              exportToCSV();
              handleCloseMenu();
            }}
            sx={{
              width: "113px",
              fontSize: "10px",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <InsertDriveFileIcon
              style={{
                marginRight: 8,
                color: theme.palette.primary.main,
              }}
            />
            CSV
          </MenuItem>
          <MenuItem
            onClick={() => {
              exportToXML();
              handleCloseMenu();
            }}
            sx={{
              width: "113px",
              fontSize: "10px",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <TextSnippetIcon
              style={{
                marginRight: 8,
                color: theme.palette.primary.main,
              }}
            />
            XML
          </MenuItem>
          <MenuItem
            onClick={() => {
              exportToJSON();
              handleCloseMenu();
            }}
            sx={{
              width: "113px",
              fontSize: "10px",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <DescriptionIcon
              style={{
                marginRight: 8,
                color: theme.palette.primary.main,
              }}
            />
            JSON
          </MenuItem>
        </Menu>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={handleSBClose}
          message="Data saved"
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleSBClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: "calc(100% + 10px)",
          }}
        />
      </Box>

      {/* Tabs + content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem",
          boxSizing: "border-box",
          height: "100%",
          mr: 2,
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              height: "2px",
              borderBottom: "2px solid blue",
            },
            mb: 2,
          }}
        >
          {/* Fields tab */}
          <Tab
            label={
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1rem",
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: "bold",
                  left: "-15px",
                  textTransform: "none",
                }}
              >
                Fields
              </Typography>
            }
          />

          {/* Tables tab only if we actually have Line Items / Items */}
          {hasTables && (
            <Tab
              label={
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  Tables
                </Typography>
              }
            />
          )}

          {/* Nested tab */}
          {nestedTab && (
            <Tab
              label={
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  {nestedTab.label}
                </Typography>
              }
            />
          )}
        </Tabs>

        {/* Fields */}
        {tabIndex === 0 && (
          <Table
            sx={{
              minWidth: 300,
              marginBottom: "1rem",
              tableLayout: "auto",
            }}
          >
            <TableBody>
              {selectedPdfData &&
                Object.entries(selectedPdfData)
                  .filter(([key]) => key !== "Items" && key !== "Line Items")
                  .map(([field, value], subIndex) => {
                    const cellKey = `${subIndex}-${field}`;
                    const isArray = Array.isArray(value);
                    const isPlainObject =
                      typeof value === "object" &&
                      value !== null &&
                      !Array.isArray(value);
                    const displayValue =
                      editedValues[cellKey] ??
                      (typeof value === "object"
                        ? JSON.stringify(value, null, 2)
                        : value);
                    const formattedLabel = formatFieldName(field);
                    const isPdfOrXmpKey = field === "PDF" || field === "XMP";

                    return (
                      <TableRow
                        key={cellKey}
                        onMouseEnter={() => setHoveredRow(cellKey)}
                        onMouseLeave={() => setHoveredRow(null)}
                        sx={{
                          transition: "background-color 0.15s ease",
                          backgroundColor:
                            hoveredRow === cellKey ? "#f9f9f9" : "inherit",
                        }}
                      >
                        <TableCell
                          sx={{
                            padding: "8px 12px",
                            border: "none",
                            width: "30%",
                            minWidth: 140,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            verticalAlign: "top",
                          }}
                          title={formattedLabel}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "bold",
                            }}
                          >
                            {formattedLabel}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            padding: "8px 12px",
                            border: "none",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            verticalAlign: "top",
                            overflowX: "hidden",
                            position: "relative",
                          }}
                        >
                          {editingCell === cellKey ? (
                            <TextareaAutosize
                              autoFocus
                              onFocus={handleFocus}
                              minRows={getRowCount(value)}
                              maxRows={10}
                              value={editedValues[cellKey] ?? displayValue}
                              onChange={(e) =>
                                setEditedValues((prev) => ({
                                  ...prev,
                                  [cellKey]: e.target.value,
                                }))
                              }
                              onBlur={() => handleSaveEdit(cellKey, field)}
                              style={{
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "14px",
                                resize: "vertical",
                                width: "100%",
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            />
                          ) : isArray ? (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                bgcolor: "#1E3899",
                                color: "#fff",
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: "none",
                              }}
                              onClick={() => openNested(field, value)}
                            >
                              View
                            </Button>
                          ) : isPlainObject ? (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                bgcolor: "#1E3899",
                                color: "#fff",
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: "none",
                              }}
                              onClick={() => openNested(field, value)}
                            >
                              View
                            </Button>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontFamily: "'Poppins', sans-serif",
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {displayValue}
                            </Typography>
                          )}

                          {!isArray && !isPlainObject && !isPdfOrXmpKey && (
                            <Box sx={overlayActionsSx(cellKey)}>
                              <Tooltip title="View" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleView(editedValues[cellKey] ?? value)
                                  }
                                  sx={{
                                    backgroundColor: "#03A9F4",
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: "#0288D1",
                                    },
                                    width: 24,
                                    height: 24,
                                    p: 0.3,
                                  }}
                                >
                                  <VisibilityIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingCell(cellKey);
                                    setEditedValues((prev) => ({
                                      ...prev,
                                      [cellKey]:
                                        prev[cellKey] !== undefined
                                          ? prev[cellKey]
                                          : typeof value === "object"
                                            ? JSON.stringify(value, null, 2)
                                            : value,
                                    }));
                                  }}
                                  sx={{
                                    backgroundColor: "#FF9800",
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: "#FB8C00",
                                    },
                                    width: 24,
                                    height: 24,
                                    p: 0.3,
                                  }}
                                >
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Reset" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleReset(cellKey, field)
                                  }
                                  sx={{
                                    backgroundColor: "#9C27B0",
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: "#7B1FA2",
                                    },
                                    width: 24,
                                    height: 24,
                                    p: 0.3,
                                  }}
                                >
                                  <ReplayIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        )}

        {/* Tables tab */}
        {tabIndex === 1 && hasTables && (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 300, marginBottom: "1rem" }} size="small">
              <TableBody>
                {(() => {
                  const tableData = Array.isArray(selectedPdfData?.Items)
                    ? selectedPdfData.Items
                    : Array.isArray(selectedPdfData?.["Line Items"])
                      ? selectedPdfData["Line Items"]
                      : [];

                  return tableData.map((subItem, subIndex) => (
                    <Accordion key={subIndex} defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "#fff",
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        Item #{subIndex + 1}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small" sx={{ minWidth: 300 }}>
                          <TableBody>
                            {Object.entries(subItem).map(
                              ([field, value], fieldIndex) => {
                                const cellKey = `${subIndex}-${field}`;
                                const isArray = Array.isArray(value);
                                const isPlainObject =
                                  typeof value === "object" &&
                                  value !== null &&
                                  !Array.isArray(value);
                                const displayValue =
                                  editedValues[cellKey] ??
                                  (typeof value === "object"
                                    ? JSON.stringify(value, null, 2)
                                    : value);
                                const formattedLabel =
                                  formatFieldName(field);
                                const isPdfOrXmpKey =
                                  field === "PDF" || field === "XMP";

                                return (
                                  <TableRow
                                    key={cellKey}
                                    onMouseEnter={() =>
                                      setHoveredRow(cellKey)
                                    }
                                    onMouseLeave={() =>
                                      setHoveredRow(null)
                                    }
                                    sx={{
                                      transition:
                                        "background-color 0.15s ease",
                                      backgroundColor:
                                        hoveredRow === cellKey
                                          ? "#f9f9f9"
                                          : fieldIndex % 2 === 0
                                            ? "#f9f9f9"
                                            : "#fff",
                                    }}
                                  >
                                    <TableCell
                                      align="left"
                                      sx={{
                                        padding: "10px 12px",
                                        fontSize: "12px",
                                        fontFamily:
                                          "'Poppins', sans-serif",
                                        fontWeight: "bold",
                                        width: "35%",
                                        minWidth: 140,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        verticalAlign: "top",
                                      }}
                                      title={formattedLabel}
                                    >
                                      {formattedLabel}
                                    </TableCell>

                                    <TableCell
                                      align="left"
                                      sx={{
                                        padding: "10px 12px",
                                        fontSize: "12px",
                                        fontFamily:
                                          "'Poppins', sans-serif",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        verticalAlign: "top",
                                        overflowX: "hidden",
                                        position: "relative",
                                      }}
                                    >
                                      {editingCell === cellKey ? (
                                        <TextareaAutosize
                                          onFocus={handleFocus}
                                          minRows={getRowCount(value)}
                                          maxRows={10}
                                          value={displayValue}
                                          onChange={(e) =>
                                            setEditedValues((prev) => ({
                                              ...prev,
                                              [cellKey]: e.target.value,
                                            }))
                                          }
                                          onBlur={() =>
                                            handleSaveEdit(
                                              cellKey,
                                              field,
                                              subIndex
                                            )
                                          }
                                          style={{
                                            padding: "8px",
                                            border: "1px solid #ccc",
                                            borderRadius: "4px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            width: "100%",
                                            fontFamily:
                                              "'Poppins', sans-serif",
                                          }}
                                        />
                                      ) : isArray ? (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() =>
                                            openNested(
                                              field,
                                              value,
                                              subIndex
                                            )
                                          }
                                        >
                                          View Nested Data (
                                          {(value || []).length})
                                        </Button>
                                      ) : isPlainObject ? (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() =>
                                            openNested(
                                              field,
                                              value,
                                              subIndex
                                            )
                                          }
                                        >
                                          View Nested Data
                                        </Button>
                                      ) : (
                                        <Typography
                                          sx={{
                                            fontSize: "12px",
                                            fontFamily:
                                              "'Poppins', sans-serif",
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {displayValue}
                                        </Typography>
                                      )}

                                      {!isPlainObject &&
                                        !isArray &&
                                        !isPdfOrXmpKey && (
                                          <Box
                                            sx={overlayActionsSx(
                                              cellKey
                                            )}
                                          >
                                            <Tooltip
                                              title="View"
                                              arrow
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleView(
                                                    editedValues[
                                                    cellKey
                                                    ] ?? value
                                                  )
                                                }
                                                sx={{
                                                  backgroundColor:
                                                    "#03A9F4",
                                                  color: "#fff",
                                                  "&:hover": {
                                                    backgroundColor:
                                                      "#0288D1",
                                                  },
                                                  width: 24,
                                                  height: 24,
                                                  p: 0.3,
                                                }}
                                              >
                                                <VisibilityIcon fontSize="inherit" />
                                              </IconButton>
                                            </Tooltip>

                                            <Tooltip
                                              title="Edit"
                                              arrow
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  setEditingCell(
                                                    cellKey
                                                  );
                                                  setEditedValues(
                                                    (prev) => ({
                                                      ...prev,
                                                      [cellKey]:
                                                        prev[
                                                          cellKey
                                                        ] !==
                                                          undefined
                                                          ? prev[
                                                          cellKey
                                                          ]
                                                          : typeof value ===
                                                            "object"
                                                            ? JSON.stringify(
                                                              value,
                                                              null,
                                                              2
                                                            )
                                                            : value,
                                                    })
                                                  );
                                                }}
                                                sx={{
                                                  backgroundColor:
                                                    "#FF9800",
                                                  color: "#fff",
                                                  "&:hover": {
                                                    backgroundColor:
                                                      "#FB8C00",
                                                  },
                                                  width: 24,
                                                  height: 24,
                                                  p: 0.3,
                                                }}
                                              >
                                                <EditIcon fontSize="inherit" />
                                              </IconButton>
                                            </Tooltip>

                                            <Tooltip
                                              title="Reset"
                                              arrow
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleReset(
                                                    cellKey,
                                                    field,
                                                    subIndex
                                                  )
                                                }
                                                sx={{
                                                  backgroundColor:
                                                    "#9C27B0",
                                                  color: "#fff",
                                                  "&:hover": {
                                                    backgroundColor:
                                                      "#7B1FA2",
                                                  },
                                                  width: 24,
                                                  height: 24,
                                                  p: 0.3,
                                                }}
                                              >
                                                <ReplayIcon fontSize="inherit" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        )}
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            )}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  ));
                })()}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Nested tab content */}
        {nestedTab && tabIndex === (hasTables ? 2 : 1) && (() => {
          const data = nestedTab.data;

          // array of objects
          if (Array.isArray(data)) {
            return (
              <Box sx={{ overflowX: "auto" }}>
                {data.map((row, idx) => (
                  <Accordion key={idx} defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                      sx={{
                        backgroundColor: "#1E3899",
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "13px",
                        fontWeight: "bold",
                        color: "#fff",
                      }}
                    >
                      {`Item #${idx + 1}`}
                    </AccordionSummary>

                    <AccordionDetails>
                      <Table size="small" sx={{ minWidth: 300, mb: 1 }}>
                        <TableBody>
                          {row && typeof row === "object" ? (
                            Object.entries(row).map(([k, v]) => {
                              const cellKey = `nested-${nestedTab.fieldKey}-${idx}-${k}`;
                              const originalValue =
                                v !== undefined && v !== null
                                  ? String(v)
                                  : "";
                              const displayValue =
                                editedValues[cellKey] ?? originalValue;
                              const isEditing =
                                editingCell === cellKey;
                              const isPdfOrXmpKey =
                                k === "PDF" ||
                                k === "XMP" ||
                                nestedTab.fieldKey === "PDF" ||
                                nestedTab.fieldKey === "XMP";

                              return (
                                <TableRow
                                  key={cellKey}
                                  onMouseEnter={() =>
                                    setHoveredRow(cellKey)
                                  }
                                  onMouseLeave={() =>
                                    setHoveredRow(null)
                                  }
                                  sx={{
                                    transition:
                                      "background-color 0.15s ease",
                                    backgroundColor:
                                      hoveredRow === cellKey
                                        ? "#f9f9f9"
                                        : "inherit",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      fontSize: "12px",
                                      fontFamily:
                                        "'Poppins', sans-serif",
                                      fontWeight: "bold",
                                      whiteSpace: "nowrap",
                                      minWidth: 120,
                                    }}
                                  >
                                    {formatFieldName(k)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "12px",
                                      fontFamily:
                                        "'Poppins', sans-serif",
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      verticalAlign: "top",
                                      position: "relative",
                                    }}
                                  >
                                    {isEditing ? (
                                      <TextareaAutosize
                                        onFocus={handleFocus}
                                        minRows={getRowCount(displayValue)}
                                        maxRows={10}
                                        value={displayValue}
                                        onChange={(e) =>
                                          setEditedValues((prev) => ({
                                            ...prev,
                                            [cellKey]: e.target.value,
                                          }))
                                        }
                                        onBlur={() =>
                                          handleSaveNestedArrayEdit(
                                            idx,
                                            k,
                                            cellKey
                                          )
                                        }
                                        style={{
                                          padding: "8px",
                                          border: "1px solid #ccc",
                                          borderRadius: "4px",
                                          fontSize: "14px",
                                          resize: "vertical",
                                          width: "100%",
                                          fontFamily:
                                            "'Poppins', sans-serif",
                                        }}
                                      />
                                    ) : (
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          fontFamily:
                                            "'Poppins', sans-serif",
                                          wordBreak: "break-word",
                                          whiteSpace: "pre-wrap",
                                        }}
                                      >
                                        {displayValue}
                                      </Typography>
                                    )}

                                    {!isPdfOrXmpKey && (
                                      <Box sx={overlayActionsSx(cellKey)}>
                                        <Tooltip title="View" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleView(displayValue)
                                            }
                                            sx={{
                                              backgroundColor: "#03A9F4",
                                              color: "#fff",
                                              "&:hover": {
                                                backgroundColor:
                                                  "#0288D1",
                                              },
                                              width: 24,
                                              height: 24,
                                              p: 0.3,
                                            }}
                                          >
                                            <VisibilityIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Edit" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setEditingCell(cellKey);
                                              setEditedValues((prev) => ({
                                                ...prev,
                                                [cellKey]:
                                                  prev[cellKey] ??
                                                  originalValue,
                                              }));
                                            }}
                                            sx={{
                                              backgroundColor: "#FF9800",
                                              color: "#fff",
                                              "&:hover": {
                                                backgroundColor:
                                                  "#FB8C00",
                                              },
                                              width: 24,
                                              height: 24,
                                              p: 0.3,
                                            }}
                                          >
                                            <EditIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Reset" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleResetNestedArrayEdit(idx, k, cellKey)
                                            }
                                            sx={{
                                              backgroundColor: "#9C27B0",
                                              color: "#fff",
                                              "&:hover": {
                                                backgroundColor: "#7B1FA2",
                                              },
                                              width: 24,
                                              height: 24,
                                              p: 0.3,
                                            }}
                                          >
                                            <ReplayIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontSize: "12px",
                                  fontFamily:
                                    "'Poppins', sans-serif",
                                  fontWeight: "bold",
                                  minWidth: 120,
                                }}
                              >
                                Value
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: "12px",
                                  fontFamily:
                                    "'Poppins', sans-serif",
                                }}
                              >
                                {String(row ?? "")}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            );
          }

          // plain object in nested tab
          if (data && typeof data === "object") {
            return (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 300, mb: 1 }}>
                  <TableBody>
                    {Object.entries(data).map(([k, v], idx) => {
                      const cellKey = `nested-${nestedTab.fieldKey}-root-${k}`;
                      const originalValue =
                        v !== undefined && v !== null
                          ? typeof v === "object"
                            ? JSON.stringify(v, null, 2)
                            : String(v)
                          : "";
                      const displayValue =
                        editedValues[cellKey] ?? originalValue;
                      const isEditing = editingCell === cellKey;
                      const isPdfOrXmpKey =
                        k === "PDF" ||
                        k === "XMP" ||
                        nestedTab.fieldKey === "PDF" ||
                        nestedTab.fieldKey === "XMP";

                      return (
                        <TableRow
                          key={idx}
                          onMouseEnter={() => setHoveredRow(cellKey)}
                          onMouseLeave={() => setHoveredRow(null)}
                          sx={{
                            transition: "background-color 0.15s ease",
                            backgroundColor:
                              hoveredRow === cellKey
                                ? "#f9f9f9"
                                : "inherit",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              minWidth: 120,
                            }}
                          >
                            {formatFieldName(k)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "12px",
                              fontFamily: "'Poppins', sans-serif",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              verticalAlign: "top",
                              position: "relative",
                            }}
                          >
                            {isEditing ? (
                              <TextareaAutosize
                                onFocus={handleFocus}
                                minRows={getRowCount(displayValue)}
                                maxRows={10}
                                value={displayValue}
                                onChange={(e) =>
                                  setEditedValues((prev) => ({
                                    ...prev,
                                    [cellKey]: e.target.value,
                                  }))
                                }
                                onBlur={() =>
                                  handleSaveNestedObjectEdit(k, cellKey)
                                }
                                style={{
                                  padding: "8px",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  fontSize: "14px",
                                  resize: "vertical",
                                  width: "100%",
                                  fontFamily: "'Poppins', sans-serif",
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontFamily: "'Poppins', sans-serif",
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {displayValue}
                              </Typography>
                            )}

                            {!isPdfOrXmpKey && (
                              <Box sx={overlayActionsSx(cellKey)}>
                                <Tooltip title="View" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleView(displayValue)
                                    }
                                    sx={{
                                      backgroundColor: "#03A9F4",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#0288D1",
                                      },
                                      width: 24,
                                      height: 24,
                                      p: 0.3,
                                    }}
                                  >
                                    <VisibilityIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Edit" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingCell(cellKey);
                                      setEditedValues((prev) => ({
                                        ...prev,
                                        [cellKey]:
                                          prev[cellKey] ?? originalValue,
                                      }));
                                    }}
                                    sx={{
                                      backgroundColor: "#FF9800",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#FB8C00",
                                      },
                                      width: 24,
                                      height: 24,
                                      p: 0.3,
                                    }}
                                  >
                                    <EditIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Reset" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleResetNestedObjectEdit(k, cellKey)
                                    }
                                    sx={{
                                      backgroundColor: "#9C27B0",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#7B1FA2",
                                      },
                                      width: 24,
                                      height: 24,
                                      p: 0.3,
                                    }}
                                  >
                                    <ReplayIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            );
          }

          // fallback: primitive
          return (
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {String(data ?? "")}
            </Typography>
          );
        })()}
      </Box>
    </>
  );
};

export default PdfDataPanel;
